import { useEffect, useRef, useState } from "react";
import {
  Info, Truck, Package, Wrench, Phone, ArrowSquareOut, Warning, X,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { alertMeta, ALERT_TONE_CLASSES } from "@/lib/customerAlerts";
import { FavoriteButton } from "@/lib/useFavorites";
import { useCustomerDrawer } from "@/lib/useCustomerDrawer";

/**
 * CustomerSnapshot — V1.1.1 OP-004 · popover triggered anywhere a
 * customer name appears. Shows the "everything I need on this call"
 * summary without a page navigation.
 *
 * Usage:
 *   <CustomerSnapshotTrigger customerId="…"><YourLink/></CustomerSnapshotTrigger>
 *
 * Or standalone via the small "i" icon inside CustomerLink.
 */
export default function CustomerSnapshotTrigger({
  customerId,
  accountNumber,
  children,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!customerId && !accountNumber) return children || null;

  return (
    <span ref={wrapRef} className="relative inline-flex items-center gap-1">
      {children}
      <button
        type="button"
        data-testid={testid || `customer-snapshot-trigger-${customerId || accountNumber}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border border-transparent text-slate-400 transition hover:border-slate-300 hover:bg-white hover:text-slate-600 ${
          open ? "border-slate-300 bg-white text-slate-600" : ""
        }`}
        aria-label="Customer snapshot"
        title="Quick snapshot"
      >
        <Info size={12} weight="bold" />
      </button>
      {open && (
        <SnapshotPopover
          customerId={customerId}
          accountNumber={accountNumber}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

function SnapshotPopover({ customerId, accountNumber, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openCustomer } = useCustomerDrawer();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let id = customerId;
        if (!id && accountNumber) {
          const r = await api.get(`/customers/by-account/${encodeURIComponent(accountNumber)}`);
          id = r.data.id;
        }
        if (!id) throw new Error("No customer id");
        const r = await api.get(`/customers/${id}/snapshot`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load snapshot");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [customerId, accountNumber]);

  return (
    <div
      data-testid="customer-snapshot-popover"
      className="absolute left-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-md border border-slate-200 bg-white text-slate-900 shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Customer Snapshot
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
          data-testid="customer-snapshot-close"
          aria-label="Close"
        >
          <X size={12} weight="bold" />
        </button>
      </div>

      {loading && (
        <div className="p-6 text-center text-xs text-slate-500">Loading…</div>
      )}
      {error && (
        <div className="p-3 text-sm text-amber-800">{error}</div>
      )}

      {data && !loading && (
        <div className="p-3 text-sm">
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div
                data-testid="customer-snapshot-name"
                className="truncate text-sm font-semibold text-slate-900"
              >
                {data.customer.customer_name}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                <span className="font-mono">#{data.customer.account_number}</span>
                {data.customer.source === "quick" && (
                  <span
                    data-testid="customer-snapshot-source"
                    className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700"
                  >
                    Quick
                  </span>
                )}
                {data.customer.status !== "Active" && (
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">
                    {data.customer.status}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <FavoriteButton
                customerId={data.customer.id}
                customerName={data.customer.customer_name}
                testid={`customer-snapshot-favorite-${data.customer.id}`}
              />
              <button
                onClick={() => { onClose(); openCustomer(data.customer.id); }}
                data-testid="customer-snapshot-open"
                className="rounded-md border border-slate-300 bg-white p-1 text-slate-600 hover:bg-slate-50"
                title="Open Customer 360"
              >
                <ArrowSquareOut size={12} weight="bold" />
              </button>
            </div>
          </div>

          {(data.alert_flags?.length > 0 || data.special_instructions) && (
            <SnapshotAlerts
              flags={data.alert_flags || []}
              instructions={data.special_instructions}
            />
          )}

          <dl className="mt-2 space-y-1.5">
            {data.customer.phone_number && (
              <Row icon={<Phone size={11} weight="bold" />} label="Phone" testid="customer-snapshot-phone">
                <a
                  href={`tel:${data.customer.phone_number}`}
                  className="font-mono text-[13px] text-sky-700 hover:underline"
                >
                  {data.customer.phone_number}
                </a>
              </Row>
            )}
            {data.last_delivery && (
              <Row icon={<Truck size={11} weight="bold" />} label="Last delivery" testid="customer-snapshot-last-delivery">
                <div className="text-[12px]">
                  <span className="font-mono">#{data.last_delivery.order_number}</span>
                  <span className="ml-1 text-slate-500">
                    · {shortDate(data.last_delivery.date)}
                  </span>
                </div>
                {(data.last_delivery.driver_names || []).length > 0 && (
                  <div className="text-[11px] text-slate-500">
                    Driver{data.last_delivery.driver_names.length > 1 ? "s" : ""}: {data.last_delivery.driver_names.join(", ")}
                  </div>
                )}
              </Row>
            )}
            {data.assigned_driver && (
              <Row icon={<Truck size={11} weight="bold" />} label="Assigned driver" testid="customer-snapshot-driver">
                {data.assigned_driver}
              </Row>
            )}
            {data.delivery_type && (
              <Row icon={<Package size={11} weight="bold" />} label="Delivery type" testid="customer-snapshot-delivery-type">
                {data.delivery_type}
              </Row>
            )}
            {data.tank_capacity && (
              <Row icon={<Package size={11} weight="bold" />} label="Tank capacity" testid="customer-snapshot-tank-capacity">
                {data.tank_capacity}
              </Row>
            )}
            {(data.products || []).length > 0 && (
              <Row icon={<Package size={11} weight="bold" />} label="Products" testid="customer-snapshot-products">
                <span className="text-[12px]">{data.products.join(", ")}</span>
              </Row>
            )}
            <Row icon={<Wrench size={11} weight="bold" />} label="Open tasks" testid="customer-snapshot-open-tasks">
              <span className={data.open_tasks_count > 0 ? "font-semibold text-amber-700" : "text-slate-500"}>
                {data.open_tasks_count}
              </span>
            </Row>
          </dl>
        </div>
      )}
    </div>
  );
}

function Row({ icon, label, testid, children }) {
  return (
    <div className="flex items-start gap-2">
      <dt className="flex items-center gap-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {icon} {label}
      </dt>
      <dd data-testid={testid} className="ml-auto min-w-0 text-right text-slate-900">
        {children}
      </dd>
    </div>
  );
}

function SnapshotAlerts({ flags, instructions }) {
  return (
    <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 p-2">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
        <Warning size={11} weight="fill" /> Operational alerts
      </div>
      {flags.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {flags.map((f) => {
            const meta = alertMeta(f);
            return (
              <span
                key={f}
                data-testid={`customer-snapshot-flag-${f}`}
                className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${ALERT_TONE_CLASSES[meta.tone] || ALERT_TONE_CLASSES.slate}`}
              >
                {meta.label}
              </span>
            );
          })}
        </div>
      )}
      {instructions && (
        <div
          data-testid="customer-snapshot-instructions"
          className="mt-1 whitespace-pre-line text-[12px] text-amber-900"
        >
          {instructions}
        </div>
      )}
    </div>
  );
}

function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}
